import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, MessageSquare, ExternalLink } from 'lucide-react';

/**
 * ReconfirmationIndicator Component
 * 
 * Displays a badge indicating that a video is a reconfirmation in response to a clarification request.
 * Shows link to original video and the clarification reason.
 * 
 * Requirements: 6.7
 */
export default function ReconfirmationIndicator({
  video,
  originalVideo,
  clarificationRequest,
  agreementId
}) {
  const { token } = useAuth();

  if (!video?.reconfirmation_of) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Reconfirmation Badge */}
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-300">
          <RefreshCw className="w-3 h-3 mr-1" />
          Reconfirmation
        </Badge>
        <span className="text-xs text-muted-foreground">
          Submitted in response to clarification request
        </span>
      </div>

      {/* Clarification Context */}
      {clarificationRequest && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-900">Clarification Requested</span>
          </div>
          <p className="text-xs text-amber-700">
            <strong>Reason:</strong> {clarificationRequest.reason}
          </p>
          <p className="text-xs text-muted-foreground">
            Requested on {new Date(clarificationRequest.requested_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* Link to Original Video */}
      {originalVideo && (
        <div className="border-l-4 border-amber-400 pl-3 py-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">Original Video</p>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${originalVideo.storage_path}?auth=${token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
          >
            <ExternalLink className="w-3 h-3" />
            View Original Confirmation
          </a>
          <p className="text-xs text-muted-foreground mt-1">
            Submitted on {new Date(originalVideo.created_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* Sequence Information */}
      {video.sequence_number && (
        <div className="text-xs text-muted-foreground">
          Video #{video.sequence_number} in confirmation sequence
        </div>
      )}
    </div>
  );
}
